import React, { useState, useCallback, memo, useRef, useEffect } from 'react';
import { closingAPI } from 'services/apisService';
import KPIPagination from './KPITable/Pagination';
import CommentCell from './KPITable/CommentCell';
import CompactTarget from './KPITable/CompactTarget';
import { salesLeadAPI } from 'services/apisService';
import ClosedWonModal from './ClosedWonModal';
import CustomSelect from './Dashboard/CustomSelect';
import UserListDropdown from './Contact/UserListDropdown';

const COLUMN_WIDTHS = {
  number: '80px',
  company: '200px', 
  amount: '150px',
  created_at: '140px',
  comment: '300px'
};

const ROW_HEIGHT = '60px';
const HEADER_HEIGHT = '48px';
const ITEMS_PER_PAGE = 10;

const formatCurrency = (amount) => `₹ ${amount.toLocaleString('en-IN')}`;
const formatDate = (dateString) => new Date(dateString).toLocaleDateString('en-US', {
  year: 'numeric',
  month: 'short', 
  day: 'numeric'
});


const TableCell = memo(({ children, width, className = "" }) => (
  <td className={`border-r text-gray-800 ${className}`} style={{ width, minWidth: width }}>
    <div className="h-full flex items-center px-4">
      {children}
    </div>
  </td>
));

const HeaderCell = memo(({ children, width, isLast = false }) => (
  <th 
    className={`py-3 px-4 text-left ${!isLast ? 'border-r' : ''} relative text-white font-semibold text-[0.85rem]`}
    style={{ width, minWidth: width }}
  >
    <div className="flex justify-between items-center">
      <span>{children}</span>
      <span className="text-white font-semibold text-lg">⋮</span>
    </div>
  </th>
));

const TableHeader = memo(() => (
  <thead>
    <tr className="bg-[#3985b6] border-b" style={{ height: HEADER_HEIGHT }}>
      <HeaderCell width={COLUMN_WIDTHS.number}>NO.</HeaderCell>
      <HeaderCell width={COLUMN_WIDTHS.company}>COMPANY</HeaderCell>
      <HeaderCell width={COLUMN_WIDTHS.amount}>AMOUNT</HeaderCell>
      <HeaderCell width={COLUMN_WIDTHS.created_at}>CREATED AT</HeaderCell>
      <HeaderCell width={COLUMN_WIDTHS.comment} isLast>COMMENT</HeaderCell>
    </tr>
  </thead>
));

const TableRow = memo(({ lead, index, currentPage, itemsPerPage }) => {
  const rowNumber = (currentPage - 1) * itemsPerPage + index + 1;

  return (
    <tr className="border-b hover:bg-gray-50" style={{ height: ROW_HEIGHT }}>
      <TableCell width={COLUMN_WIDTHS.number}>
        <span className="text-gray-600 font-medium">{rowNumber}</span>
      </TableCell>
      <TableCell width={COLUMN_WIDTHS.company}>
        <span className="text-[#3985b6] font-medium">{lead.contact.companyName}</span>
      </TableCell>
      <TableCell width={COLUMN_WIDTHS.amount}>
        <span className="font-semibold text-green-600">
          {formatCurrency(lead.amount)}
        </span>
      </TableCell>
      <TableCell width={COLUMN_WIDTHS.created_at}>
        {formatDate(lead.created_at)}
      </TableCell>
      <td className="text-gray-700 text-base" style={{ width: COLUMN_WIDTHS.comment, minWidth: COLUMN_WIDTHS.comment }}>
        <div className="h-full flex items-center px-4">
          <CommentCell comment={lead.comment} />
        </div>
      </td>
    </tr>
  );
});

const LoadingSpinner = memo(() => (
  <div className="flex justify-center items-center py-8">
    <div className="animate-spin rounded-full h-9 w-9 border-b-2 border-[#3985b6]" />
  </div>
));

const ErrorMessage = memo(({ message, onRetry }) => (
  <div className="bg-red-50 border border-red-200 rounded-md p-4">
    <div className="flex items-center">
      <div className="text-red-600 mr-2">⚠️</div>
      <div>
        <div className="font-medium text-red-800">Error loading data</div>
        <div className="text-sm text-red-600">{message}</div>
      </div>
    </div>
    {onRetry && (
      <button 
        onClick={onRetry}
        className="mt-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm transition-colors"
      >
        Try Again
      </button>
    )}
  </div>
));

const EmptyState = memo(() => (
  <div className="bg-white p-8 text-center rounded-md">
    <div className="text-gray-500 text-lg">No closed leads found</div>
    <div className="text-gray-400 text-sm mt-2">Start closing some deals to see them here!</div>
  </div>
));

const useClosingLeads = () => {
  const [state, setState] = useState({
    data: [],
    loading: true,
    error: null,
    currentPage: 1,
    totalPages: 1,
    total: 0,
    limit: ITEMS_PER_PAGE,
    monthlyTarget: { target: 0, achevied: 0 },
    selectedUserId: null // Add selectedUserId to state
  });

  const fetchData = useCallback(async (page = 1, userId = null) => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      let result;
      if (userId) {
        const queryString = `page=${page}&limit=${state.limit}&user_id=${userId}`;
        result = await closingAPI.getClosingLeads(page, state.limit, queryString);
      } else {
        result = await closingAPI.getClosingLeads(page, state.limit);
      }
      
      setState(prev => ({
        ...prev,
        data: result.data || [],
        total: result.total || 0,
        currentPage: result.page || 1,
        totalPages: result.totalPages || 1,
        limit: result.limit || ITEMS_PER_PAGE,
        monthlyTarget: result.monthlyTarget || { target: 0, achevied: 0 },
        loading: false
      }));
    } catch (err) {
      setState(prev => ({
        ...prev,
        error: err.message || 'Failed to fetch data',
        loading: false
      }));
      console.error('Error fetching closing leads:', err);
    }
  }, [state.limit]);

  const handlePageChange = useCallback((page) => {
    fetchData(page, state.selectedUserId);
  }, [fetchData, state.selectedUserId]);

  const handleRetry = useCallback(() => {
    fetchData(state.currentPage, state.selectedUserId);
  }, [fetchData, state.currentPage, state.selectedUserId]);

  const handleUserSelect = useCallback((userId) => {
    setState(prev => ({ ...prev, selectedUserId: userId, currentPage: 1 }));
    fetchData(1, userId);
  }, [fetchData]);

  const clearUserFilter = useCallback(() => {
    setState(prev => ({ ...prev, selectedUserId: null, currentPage: 1 }));
    fetchData(1, null);
  }, [fetchData]);

  useEffect(() => {
    fetchData(1);
  }, [fetchData]);

  return {
    ...state,
    handlePageChange,
    handleRetry,
    fetchData,
    handleUserSelect,
    clearUserFilter
  };
};

const KPITable = () => {
  const {
    data,
    loading,
    error,
    currentPage,
    totalPages,
    limit,
    monthlyTarget,
    selectedUserId,
    handlePageChange,
    handleRetry,
    fetchData,
    handleUserSelect,
    clearUserFilter
  } = useClosingLeads();

  const [showClosedWonModal, setShowClosedWonModal] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [showSelect, setShowSelect] = useState(false);

  const [showUsersDropdown, setShowUsersDropdown] = useState(false);
  const usersDropdownRef = useRef(null);

  const fetchCompanies = async () => {
    setLoadingCompanies(true);
    try {
      const result = await salesLeadAPI.getSalesLeads(1, 50);
      setCompanies(result.data || []);
    } catch (error) {
      console.error('Error fetching companies:', error);
    } finally {
      setLoadingCompanies(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  const handleCreateClick = () => {
    setShowSelect(true);
  };

  const handleCompanyChange = (e) => {
    const selectedCompanyData = companies.find(c => c.Contact.id === e.target.value);
    if (selectedCompanyData) {
      setSelectedCompany(selectedCompanyData);
      setShowClosedWonModal(true);
      setShowSelect(false); 
    }
  };

  const handleSelectToggle = (isOpen) => {
    if (!isOpen) {
      setShowSelect(false);
    }
  };

  const handleClosedWonSubmit = async (formData) => {
    try {
      setShowClosedWonModal(false);
      setSelectedCompany(null);
      fetchData(currentPage, selectedUserId);
    } catch (error) {
      console.error('Error creating closing lead:', error);
    }
  };

  const handleModalClose = () => {
    setShowClosedWonModal(false);
    setSelectedCompany(null);
  };

  const handleUserSelectFromDropdown = (user) => {
    handleUserSelect(user.id);
    setShowUsersDropdown(false);
  };

  const renderContent = () => {
    if (loading) return <LoadingSpinner />;
    if (error) return <ErrorMessage message={error} onRetry={handleRetry} />;
    if (data.length === 0) return <EmptyState />;

    return (
      <div className="overflow-x-auto rounded-[5px]">
        <table className="w-full bg-white border-collapse" style={{ tableLayout: 'fixed', borderSpacing: 0 }}>
          <TableHeader />
          <tbody>
            {data.map((lead, index) => (
              <TableRow 
                key={lead.id} 
                lead={lead} 
                index={index} 
                currentPage={currentPage}
                itemsPerPage={limit}
              />
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="bg-[#bbd4e6] p-4 rounded-lg">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-gray-800">Closed Leads</h2>
        
        <div className="flex items-center gap-4">
          <div className="w-64 relative">
            {!showSelect ? (
              <button
                onClick={handleCreateClick}
                disabled={loadingCompanies}
                className="w-64 px-4 py-[8px] bg-[#3985b6] text-white rounded-lg hover:bg-[#2d6a8f] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loadingCompanies ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                ) : (
                  <>
                    <span>Create</span>
                  </>
                )}
              </button>
            ) : (
              <CustomSelect
                value=""
                onChange={handleCompanyChange}
                onToggle={handleSelectToggle}
                isFromKPITable={true}
                options={companies.map(company => ({
                  value: company.Contact.id,
                  companyName: company.Contact.companyName,
                  contactName: company.Contact.name,
                  searchText: `${company.Contact.companyName} ${company.Contact.name}`
                }))}
                placeholder="Select a company..."
                name="company"
                isKPITable={true}
                autoOpen={true}
              />
            )}
          </div>
          
          <CompactTarget 
            target={monthlyTarget.target} 
            achieved={monthlyTarget.achevied} 
          />
          
          {/* Add UserListDropdown here at the rightmost position */}
          <UserListDropdown
            usersDropdownRef={usersDropdownRef}
            setShowUsersDropdown={setShowUsersDropdown}
            showUsersDropdown={showUsersDropdown}
            selectedUserId={selectedUserId}
            handleUserSelect={handleUserSelectFromDropdown}
            clearUserFilter={clearUserFilter}
            apiType="closing" 
          />
        </div>
      </div>
      
      <div className="rounded-md">
        {renderContent()}
      </div>
      
      {!loading && !error && totalPages > 1 && (
        <KPIPagination 
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}

      {/* Closed Won Modal */}
      <ClosedWonModal
        isOpen={showClosedWonModal}
        onClose={handleModalClose}
        onSubmit={handleClosedWonSubmit}
        leadData={{
          name: selectedCompany?.Contact?.name,
          companyName: selectedCompany?.Contact?.companyName,
          id: selectedCompany?.Contact?.id,
          leadContactId: selectedCompany?.Contact?.id,
          userId: selectedCompany?.user?.id  
        }}
        isFromKPITable={true}
      />
    </div>
  );
};

export default KPITable;